import { useMemo, useState, useRef, useEffect } from 'react'
import { Table } from 'antd'
import { useSpring, animated } from 'react-spring'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import {
  renderTranslation,
  handleMultipleTableScroll,
  getCurrencySymbolByPricelist,
} from '../../Helpers/GenericHelpers'
import {
  priceStandardCellPriceStoresWidth,
  priceStandardCellPriceWidth,
  priceStandardHeight,
} from '../../Constants/TableConfiguration'
import PricesListingHeader from './PricesListingHeader'
import { useAppDispatch, useAppSelector } from '../../hooks'
import {
  PriceTypeSave,
  basePricelist,
  getBasePriceForStore,
  getListingBySize,
  getListingVat,
  hasDiscountStoresRows,
} from '../../Helpers/PricesHelper'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import InputNumberDiwa from 'Components/Generic/InputNumberDiwa'
import { setPriceChanged } from 'Slices/Kiosk/PriceSlice'
import { updateBasePriceValue } from 'Slices/Kiosk/ArticleDetailSlice'

function PricesListing(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const basePriceData: basePricelist[] = useAppSelector(
    state => state.present.articleDetail.articleSelected.basePriceData,
  )

  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const priceTypeFilter = useAppSelector(state => state.present.price.priceTypeFilter)
  const isListingTypeSell = props.activeListing
    ? basePriceData.find(x => x.name === props.activeListing)?.isSell
    : false
  const priceList1 = useAppSelector(state => state.present.tableOptions.priceList1)
  const channelType: TypeOfChannel = useAppSelector(state => state.present.price.channelType)

  const [isColumnOpen, setIsColumnOpen] = useState(true)
  const [isColumnMinified, setIsColumnMinified] = useState(false)
  const [componentWidth, setComponentWidth] = useState(0)
  const priceListingTableRef = useRef<any>(undefined)

  const [isBySize, hasVat] = useMemo(() => {
    return [getListingBySize(basePriceData), getListingVat(basePriceData, props.activeListing)]
  }, [basePriceData, priceTypeFilter, props.activeListing])

  const cellPriceWidth = useMemo(() => {
    return channelType == TypeOfChannel.Store && hasDiscountStoresRows(basePriceData)
      ? priceStandardCellPriceStoresWidth
      : priceStandardCellPriceWidth
  }, [channelType, basePriceData])

  const currentPriceData = useMemo(() => {
    return props.activeListing && basePriceData
      ? basePriceData.find(x => x.name === props.activeListing)
      : ({} as basePricelist)
  }, [basePriceData, props.activeListing])

  const listingChildren = useMemo(() => {
    return [
      {
        title: (
          <div className='line-height-32 text-right pr-1 d-flex justify-center' style={{ width: cellPriceWidth }}>
            <span className='pr-1'>{getCurrencySymbolByPricelist(priceList1, props.activeListing)}</span>
            <span>
              {`(${renderTranslation(language, TranslationKeys.listingPopListingVat)}: ${
                hasVat
                  ? renderTranslation(language, TranslationKeys.listingPopListingVatYes)
                  : renderTranslation(language, TranslationKeys.listingPopListingVatNo)
              })`}
            </span>
          </div>
        ),
        dataIndex: 'price',
        key: 'price',
        className: 'listing-price',
        width: cellPriceWidth,
        render: (text, record, index) => {
          const priceliting = currentPriceData.prices[record.size.sizeId]
            ? currentPriceData.prices[record.size.sizeId]
            : currentPriceData.isBySize
            ? 0
            : index === 0
            ? currentPriceData.prices[0]
            : null
          return priceliting ? (
            <InputNumberDiwa
              className={'num-input discount-input '}
              min={0}
              controls={false}
              value={isListingTypeSell ? priceliting.sellPrice : priceliting.buyPrice}
              onChange={newprice => {
                const changePayload = {
                  Price: newprice,
                  PriceType: isListingTypeSell ? PriceTypeSave.Sel : PriceTypeSave.Buy,
                  pricelist: props.activeListing,
                  SizeId: currentPriceData.isBySize ? record.size.sizeId : 0,
                }
                dispatch(setPriceChanged(changePayload))
                dispatch(updateBasePriceValue(changePayload))
              }}
            />
          ) : null
        },
      },
    ]
  }, [
    hasVat,
    isBySize,
    isListingTypeSell,
    language,
    priceList1,
    props.activeListing,
    props.priceTypeShow,
    cellPriceWidth,
  ])

  const basePrice = useMemo(() => {
    return getBasePriceForStore(basePriceData, props.activeListing)
  }, [channelType, basePriceData])

  const pricerRecordTableData = useMemo(() => {
    return basePriceData && basePriceData.length > 0
      ? Object.values(basePriceData.find(x => x.isBaseForSize).prices)
      : []
  }, [basePriceData])

  const listingTableColumns = useMemo(() => {
    return [
      {
        title: (
          <PricesListingHeader
            {...props}
            showPrice={false}
            basePrice={basePrice}
            isColumnOpen={isColumnOpen}
            isColumnMinified={isColumnMinified}
            setIsColumnOpen={setIsColumnOpen}
            language={language}
          />
        ),
        children: listingChildren,
      },
    ]
  }, [props, isColumnOpen, isColumnMinified, listingChildren, channelType])

  useEffect(() => {
    const tableBody =
      priceListingTableRef &&
      priceListingTableRef.current &&
      priceListingTableRef.current.querySelector('.ant-table-body')
    tableBody && tableBody.addEventListener('scroll', handleMultipleTableScroll, { passive: true })

    return () => {
      tableBody && tableBody.removeEventListener('scroll', handleMultipleTableScroll)
    }
  }, [articleSelected])

  useEffect(() => {
    setComponentWidth(
      (priceListingTableRef?.current?.querySelector('.ant-table-container .ant-table-thead') as HTMLElement)
        ?.offsetWidth + 1,
    )
  }, [articleSelected, props.activeListing, cellPriceWidth])

  return (
    <div className='' key={'listing-table-wrap-' + props.activeListing} ref={priceListingTableRef}>
      <animated.div
        style={useSpring({
          width: isColumnOpen ? componentWidth : 30,
          onRest: () => {
            setIsColumnMinified(!isColumnOpen)
          },
        })}
        className={'listing multi-table-slide-container' + (isColumnOpen ? ' open' : ' closed')}
      >
        <Table
          className={'listing-table multi-table same-table-scroll'}
          columns={listingTableColumns}
          dataSource={pricerRecordTableData}
          pagination={false}
          tableLayout='fixed'
          scroll={{ y: priceStandardHeight, x: '100%' }}
        />
      </animated.div>
    </div>
  )
}

export default PricesListing
